import { useMemo, useEffect } from 'react';
import { Box, HStack, Button, Flex, Icon, Text, Menu, Portal } from '@chakra-ui/react';
import { NavLink, useParams } from 'react-router-dom';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsViewer from '@/lib/hooks/use-is-viewer';
import useFeatureFlag from '@/lib/hooks/use-feature-flag';
import useContentTypesStore from '@/utils/use-content-type';
import useCollectionTypesStore from '@/utils/use-collection-type';
import { ContentType, CollectionType } from '@/lib/types';
import {
  HomeIcon,
  Cog6ToothIcon,
  Squares2X2Icon,
  Bars3Icon,
  RectangleStackIcon,
  CalendarDaysIcon,
  SparklesIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';

type IconType = React.ForwardRefExoticComponent<any>;

interface MenuProps {
  name: string;
  href: string;
  icon?: IconType;
}

// Coach Hub TopNav links are flat text links — NOT buttons. Buttons get the
// display-tier recipe (UPPERCASE, tracked); flat links stay sentence case and
// read clean. Active = workspace brand color + semibold; inactive picks up a
// subtle gray fill on hover. `textTransform: none` is belt-and-suspenders in
// case any uppercase leaks in from an ancestor.
const flatLinkSx = (isActive?: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  px: '12px',
  py: '4px',
  borderRadius: 'sm',
  fontSize: 'sm',
  whiteSpace: 'nowrap' as const,
  textTransform: 'none' as const,
  letterSpacing: 'normal',
  bg: 'transparent',
  color: isActive ? 'brand.solid' : 'fg',
  fontWeight: isActive ? '600' : '400',
  transition: 'background-color .12s, color .12s',
  cursor: 'pointer',
  _hover: isActive ? undefined : { bg: 'bg.subtle' },
});

// Anchor reset so react-router's NavLink doesn't underline the flat link.
const navLinkStyle = { textDecoration: 'none' };

function NavigationItem({ item, isActive }: { item: MenuProps; isActive?: boolean }) {
  return (
    <Flex {...flatLinkSx(isActive)}>
      {item.icon && <Icon as={item.icon} h={4} w={4} />}
      <Text as='span'>{item.name}</Text>
    </Flex>
  );
}

function DropdownMenu({
  title,
  icon,
  links,
}: {
  title: string;
  icon: IconType;
  links: MenuProps[];
}) {
  if (links.length === 0) return null;

  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Flex as='button' border='none' appearance='none' {...flatLinkSx(false)}>
          <Icon as={icon} h={4} w={4} />
          <Text as='span'>{title}</Text>
        </Flex>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>
            {links.map((link) => (
              <Menu.Item key={link.href} asChild value={link.name}>
                <NavLink to={link.href}>{link.name}</NavLink>
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

function MobileMenu({
  workspaceId,
  isInAdminGroup,
  isViewer,
  coursesEnabled,
  contentLinks,
  collectionLinks,
}: {
  workspaceId: string;
  isInAdminGroup: boolean;
  isViewer: boolean | null;
  coursesEnabled: boolean;
  contentLinks: MenuProps[];
  collectionLinks: MenuProps[];
}) {
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button variant='ghost' size='sm'>
          <Icon as={Bars3Icon} w={4} h={4} />
          Menu
        </Button>
      </Menu.Trigger>
      <Menu.Content>
        <Menu.ItemGroup>
          <Menu.Item asChild value='Home'>
            <NavLink to={`/${workspaceId}/home`}>Home</NavLink>
          </Menu.Item>
          {!isViewer && (
            <Menu.Item asChild value='Create'>
              <NavLink to={`/${workspaceId}/create`}>Create</NavLink>
            </Menu.Item>
          )}
          {coursesEnabled && (
            <Menu.Item asChild value='Courses'>
              <NavLink to={`/${workspaceId}/courses`}>
                {isViewer ? 'My courses' : 'Courses'}
              </NavLink>
            </Menu.Item>
          )}
          <Menu.Item asChild value='Planner'>
            <NavLink to={`/${workspaceId}/planner`}>Planner</NavLink>
          </Menu.Item>
          {isInAdminGroup && (
            <Menu.Item asChild value='Settings'>
              <NavLink to={`/${workspaceId}/settings`}>Settings</NavLink>
            </Menu.Item>
          )}
        </Menu.ItemGroup>

        {contentLinks.length > 0 && (
          <>
            <Menu.Separator />
            <Menu.ItemGroup>
              <Menu.ItemGroupLabel>Libraries</Menu.ItemGroupLabel>
              {contentLinks.map((item) => (
                <Menu.Item asChild key={item.href} value={item.name}>
                  <NavLink to={item.href}>{item.name}</NavLink>
                </Menu.Item>
              ))}
            </Menu.ItemGroup>
          </>
        )}

        {collectionLinks.length > 0 && (
          <>
            <Menu.Separator />
            <Menu.ItemGroup>
              <Menu.ItemGroupLabel>Collections</Menu.ItemGroupLabel>
              {collectionLinks.map((item) => (
                <Menu.Item asChild key={item.href} value={item.name}>
                  <NavLink to={item.href}>{item.name}</NavLink>
                </Menu.Item>
              ))}
            </Menu.ItemGroup>
          </>
        )}
      </Menu.Content>
    </Menu.Root>
  );
}

export default function NavigationMenu() {
  const isInAdminGroup = useIsInAdminGroup();
  const isViewer = useIsViewer();
  // Courses ship dark behind the `courses` query-param flag (see CoursesGate
  // in app.tsx) — the nav entry hides with it.
  const coursesEnabled = useFeatureFlag('courses');
  // Collections are hidden from the nav (desktop dropdown + mobile group) unless
  // the `collections` query-param flag is on — `?flag=collections` to preview.
  // The routes themselves stay reachable; only the menu entries are gated.
  const collectionsEnabled = useFeatureFlag('collections');
  const { workspaceId = '' } = useParams();
  const { contentTypes, loadContentTypes } = useContentTypesStore();
  const { collectionTypes, loadCollectionTypes } = useCollectionTypesStore();

  useEffect(() => {
    if (workspaceId) {
      loadContentTypes(workspaceId);
      if (collectionsEnabled) loadCollectionTypes(workspaceId);
    }
  }, [workspaceId, collectionsEnabled, loadContentTypes, loadCollectionTypes]);

  const { contentLinks, collectionLinks } = useMemo(() => {
    const contentItems = contentTypes.map((type: ContentType) => {
      const url = `/${workspaceId}/content-types/${type.id}/contents/workspace-library`;

      return {
        name: type.name,
        href: url,
      };
    });

    // An empty list makes both the desktop DropdownMenu and the mobile group
    // render nothing, so gating here hides Collections everywhere at once.
    const collectionItems = collectionsEnabled
      ? collectionTypes.map((type: CollectionType) => {
          const url = `/${workspaceId}/collection-types/${type.id}/collections/workspace-collections`;

          return {
            name: type.name,
            href: url,
          };
        })
      : [];

    return { contentLinks: contentItems, collectionLinks: collectionItems };
  }, [contentTypes, collectionTypes, collectionsEnabled, workspaceId]);

  const homeItem: MenuProps = {
    name: 'Home',
    href: `/${workspaceId}/home`,
    icon: HomeIcon,
  };

  const createItem: MenuProps = {
    name: 'Create',
    href: `/${workspaceId}/create`,
    icon: SparklesIcon,
  };

  const settingsItem: MenuProps = {
    name: 'Settings',
    href: `/${workspaceId}/settings`,
    icon: Cog6ToothIcon,
  };

  const plannerItem: MenuProps = {
    name: 'Planner',
    href: `/${workspaceId}/planner`,
    icon: CalendarDaysIcon,
  };

  const coursesItem: MenuProps = {
    // Viewers get no hubs — their courses page is the personal "My courses".
    name: isViewer ? 'My courses' : 'Courses',
    href: `/${workspaceId}/courses`,
    icon: AcademicCapIcon,
  };

  return (
    <>
      <HStack gap={1} display={{ base: 'none', lg: 'flex' }} align='center'>
        <NavLink to={homeItem.href} style={navLinkStyle}>
          {({ isActive }) => <NavigationItem item={homeItem} isActive={isActive} />}
        </NavLink>

        {!isViewer && (
          <NavLink to={createItem.href} style={navLinkStyle}>
            {({ isActive }) => <NavigationItem item={createItem} isActive={isActive} />}
          </NavLink>
        )}

        <DropdownMenu title='Libraries' icon={Squares2X2Icon} links={contentLinks} />

        <DropdownMenu title='Collections' icon={RectangleStackIcon} links={collectionLinks} />

        {coursesEnabled && (
          <NavLink to={coursesItem.href} style={navLinkStyle}>
            {({ isActive }) => <NavigationItem item={coursesItem} isActive={isActive} />}
          </NavLink>
        )}

        <NavLink to={plannerItem.href} style={navLinkStyle}>
          {({ isActive }) => <NavigationItem item={plannerItem} isActive={isActive} />}
        </NavLink>

        {isInAdminGroup && (
          <NavLink to={settingsItem.href} style={navLinkStyle}>
            {({ isActive }) => <NavigationItem item={settingsItem} isActive={isActive} />}
          </NavLink>
        )}
      </HStack>

      <Box display={{ base: 'block', lg: 'none' }}>
        <MobileMenu
          workspaceId={workspaceId}
          isInAdminGroup={isInAdminGroup}
          isViewer={isViewer}
          coursesEnabled={coursesEnabled}
          contentLinks={contentLinks}
          collectionLinks={collectionLinks}
        />
      </Box>
    </>
  );
}
